import get from 'lodash/get'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'
import * as Sentry from '@sentry/react'

const ENV_PRODUCTION = 'production'
const OVERRIDE_ROLE_PREFIX = 'feature:flag-override:'

// module-level, so it survives across the many `withConfig`-wrapped
// component instances (148+) that each independently compute this - without
// it, the same override would log to Sentry once per mounted instance
// instead of once per user/environment/feature-set.
const loggedEvents = new Set()

const roleToFeaturePath = (roleName) => roleName.slice(OVERRIDE_ROLE_PREFIX.length).replace(/:/g, '.')

const logOnce = (message, { paths, user, environment }) => {
  const resolvedEnvironment = environment === undefined ? 'unknown' : environment
  const dedupeKey = `${message}|${user.name}|${[...paths].sort().join(',')}|${resolvedEnvironment}`
  if (loggedEvents.has(dedupeKey)) return
  loggedEvents.add(dedupeKey)

  Sentry.withScope(scope => {
    scope.setLevel('warning')
    scope.setTag('component', 'featureRoleOverrides')
    scope.setExtra('features', paths)
    scope.setExtra('userName', user.name)
    scope.setExtra('userLocationId', get(user, 'location.id'))
    scope.setExtra('environment', resolvedEnvironment)
    Sentry.captureMessage(message)
  })
}

/**
 * applyFeatureRoleOverrides()
 *
 * Given a tenant/env `features` config object and the logged-in `user`,
 * returns a feature set where any feature the user has been granted
 * directly through a matching `feature:flag-override:<name>` role is
 * flipped on - even if it's off (or simply not mentioned at all) in config
 * (see docs/Feature-Flags.md). This is what makes the override
 * transparently apply to every `hasFeature(config, ...)` call site with no
 * per-call-site changes - it's applied once, in `withConfig`, before
 * `features` reaches any of them.
 *
 * `feature:flag-override:` is a dedicated sub-namespace, deliberately
 * distinct from ordinary `feature:` permission roles (e.g. `feature:settings`,
 * `feature:shipments:pack-shipments`) - those are a different, older
 * mechanism (see `userIsAuthorised`) and are never read here.
 *
 * Only flips a flag that isn't already truthy - the same check `hasFeature`
 * itself does (`!!get(features, path)`), just inverted. A flag that's
 * missing from config entirely is treated the same as one explicitly set to
 * `false`, since `hasFeature` can't tell those apart either - a tenant that
 * never mentions a flag has it just as off as one that spells out `false`.
 *
 * Never applies in production, or when the environment can't be determined
 * (fails closed rather than risk silently working in production). Returns
 * the original `features` reference unchanged whenever nothing applies, so
 * callers that rely on referential equality aren't affected.
 *
 * Every resolved outcome - granted or blocked - is logged to Sentry once per
 * user/feature-set/environment, since this is meant to be exceptional.
 *
 * @param {object} features - the tenant/env feature config (`config.features`)
 * @param {string} [environment] - `config.env`
 * @param {object} [user] - the logged-in user, if any
 */
export const applyFeatureRoleOverrides = (features, environment, user) => {
  if (!user || !Array.isArray(user.roles) || !user.roles.length) return features

  const paths = user.roles
    .filter(role => role.startsWith(OVERRIDE_ROLE_PREFIX))
    .map(roleToFeaturePath)
    .filter(path => !get(features, path))

  if (!paths.length) return features

  if (environment === ENV_PRODUCTION || environment === undefined) {
    logOnce('Feature flag user role override blocked (production or unknown environment)', { paths, user, environment })
    return features
  }

  logOnce('Feature enabled via user role override', { paths, user, environment })

  const overridden = cloneDeep(features)
  paths.forEach(path => set(overridden, path, true))
  return overridden
}
